import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Alert, Card } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';

const AdminRegister = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const { name, email, password, confirmPassword } = formData;

  // Check if admin already exists on component mount
  useEffect(() => {
    const checkAdminExists = async () => {
      try {
        // Try to login with a dummy admin to see if any admin exists
        await authAPI.adminLogin({ email: 'test@example.com', password: 'test123' });
        
        // If we get here, an admin exists (though login failed)
        setError('Admin account already exists. Please login instead.');
        setTimeout(() => {
          navigate('/admin/login');
        }, 3000);
      } catch (err) {
        // If we get a 404, no admin exists, which is what we want
        if (err.response?.status === 404) {
          // This is expected, no admin exists
          return;
        }
        
        // If we get a 500, there might be server issues
        if (err.response?.status === 500) {
          setError('Server error. The server might be experiencing database connection issues. Please try again later.');
        }
      }
    };
    
    checkAdminExists();
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Validate form
      if (!name || !email || !password || !confirmPassword) {
        setError('Please fill in all fields');
        setLoading(false);
        return;
      }

      if (password !== confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }

      if (password.length < 8) {
        setError('Password must be at least 8 characters');
        setLoading(false);
        return;
      }

      // Call API to register
      const response = await authAPI.registerAdmin({
        name,
        email,
        password
      });
      
      // Save token and user info to localStorage
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('userRole', 'admin');
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      setSuccess('Admin account created successfully! Redirecting to dashboard...');
      
      // Redirect to admin dashboard after a short delay
      setTimeout(() => {
        navigate('/admin');
      }, 2000);
    } catch (err) {
      console.error('Registration error:', err);
      
      if (err.response?.status === 403) {
        setError('Admin account already exists. Please login instead.');
        setTimeout(() => {
          navigate('/admin/login');
        }, 3000);
        return;
      }
      
      // Handle server error
      if (err.response?.status === 500) {
        setError('Server error. The server might be experiencing database connection issues. Please try again later.');
        return;
      }
      
      setError(
        err.response?.data?.message || 
        'Registration failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="auth-container">
      <Card>
        <Card.Body>
          <div className="auth-logo">
            <h2>Little Lambs Fundraising Online Portal</h2>
          </div>
          
          <h3 className="auth-title">Create First Admin Account</h3>
          
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={name}
                onChange={handleChange}
                placeholder="Enter your first and last name"
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Email Address</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={email}
                onChange={handleChange}
                placeholder="Enter your email"
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={password}
                onChange={handleChange}
                placeholder="Enter a password (min. 8 characters)"
                required
                minLength="8"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control
                type="password"
                name="confirmPassword"
                value={confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                required
                minLength="8"
              />
            </Form.Group>
            
            <Button 
              variant="primary" 
              type="submit" 
              className="w-100 mt-3" 
              disabled={loading}
            >
              {loading ? 'Creating Account...' : 'Create Admin Account'}
            </Button>
          </Form>
          
          <div className="auth-footer mt-4">
            <p>
              <Link to="/admin/login" className="auth-link">Back to Admin Login</Link>
            </p>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AdminRegister;
